import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { PreviousPurchaseModel } from "@rightstore/Model/PreviousPurchase.model";
import getLocalizedDate from "@rightstore/Utilities/getLocalizedDate";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    previousPurchase: PreviousPurchaseModel;
    showOnlyButton?: boolean; // This props is used to only render the button and not the whole block.
}

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = OwnProps &
    ReturnType<typeof mapStateToProps>

interface PreviousPurchaseBlockStyles {
    wrapper: InjectableCss;
    purchasedWrapper: InjectableCss;
    purchasedMessage: TypographyProps;
    validUntilMessageWrapper: InjectableCss;
    validUntilMessage: TypographyProps;
}

const styles: PreviousPurchaseBlockStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            direction: initial;
            background:${getColor("common.background")};
            box-shadow: 0px 0px 20px rgba(0, 55, 69, 0.1);
            border-radius: 16px;
            padding: 39px 33px;
            min-width: 339px;

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                            width: 100%;
                            min-width: auto;
                        `,
                    css`
                            width: 100%;
                            min-width: auto;
                        `,
                    null,
                    null,
                    null,
                ]);
            }
            }
        `,
    },
    purchasedWrapper: {
        css: css`
            margin-bottom: 16px;
            display: flex;
            align-items: start;
            flex-direction: column;
            justify-content: center;
            padding: 0px;            
        `
    },
    purchasedMessage: {
        color: '#B3B3B3',
        weight: 700,
        size: '2.5rem',
        lineHeight: '60px'
    },
    validUntilMessageWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            padding: 4px 12px;
            gap: 4px;
            background: #F0FFF5;
            border-radius: 20px;
        `
    },
    validUntilMessage: {
        size: '.9375rem',
        lineHeight: '22px',
        weight: 400,
        color: '#006147'
    }
}

const SectionWrapper = getStyledWrapper("section");

const PreviousPurchaseBlock: React.FC<Props> = ({ previousPurchase, language, showOnlyButton }) => {
    const viewProductClickHandler = () => {
        window.open(previousPurchase.fulfillmentUrl, "_blank", "noopener,noreferrer");
    };

    const validUntilFormatted = getLocalizedDate({
        date: previousPurchase.validUntil,
        language
    });

    if (showOnlyButton) {
        return (
            <>
                {previousPurchase.fulfillmentUrl != null &&
                    <Button variant="primary" onClick={viewProductClickHandler}>{translate("View product")}</Button>
                }
            </>
        )
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <SectionWrapper {...styles.purchasedWrapper}>
                <StyledWrapper>
                    <Typography {...styles.purchasedMessage} data-test-selector="purchasedText">
                        {translate("Purchased")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.validUntilMessageWrapper}>
                    <Typography {...styles.validUntilMessage} data-test-selector="validUntilMessage">
                        {translate('Available till {Date}').replace('{Date}', validUntilFormatted)}
                    </Typography>
                </StyledWrapper>
            </SectionWrapper>
            {previousPurchase.fulfillmentUrl != null &&
                <Button onClick={viewProductClickHandler}>
                    {translate("View product")}
                </Button>
            }
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(PreviousPurchaseBlock);
